// src/components/GuestRacers.js
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import { db, auth } from "../firebase";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const MAX_IN_QUERY = 10;

const chunkIds = (ids) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += MAX_IN_QUERY) {
    chunks.push(ids.slice(i, i + MAX_IN_QUERY));
  }
  return chunks;
};

const GuestRacers = () => {
  const { user } = useAuth();
  const [runs, setRuns] = useState([]);
  const [racers, setRacers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlShareGroupId = params.get("id");
  const [shareGroupId, setShareGroupId] = useState(urlShareGroupId);

  useEffect(() => {
    if (urlShareGroupId) {
      localStorage.setItem("guestShareGroupId", urlShareGroupId);
      setShareGroupId(urlShareGroupId);
      return;
    }
    const cached = localStorage.getItem("guestShareGroupId");
    if (cached) {
      setShareGroupId(cached);
    }
  }, [urlShareGroupId]);

  useEffect(() => {
    if (user || !shareGroupId) return;
    signInAnonymously(auth).catch((error) => {
      console.error("Anonymous sign-in error:", error);
    });
  }, [shareGroupId, user]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const fetchGroupName = async () => {
      const groupDocRef = doc(db, "sharegroups", shareGroupId);
      const groupSnap = await getDoc(groupDocRef);
      if (groupSnap.exists()) {
        setGroupName(groupSnap.data().name);
      } else {
        setGroupName("");
      }
    };
    fetchGroupName();
  }, [shareGroupId]);

  useEffect(() => {
    if (!shareGroupId || !user) return;
    const runsMap = new Map();
    const unsubscribers = [];

    const upsertRuns = () => {
      setRuns(Array.from(runsMap.values()));
    };

    const subscribeSharedRuns = (collectionName, filterField, filterValue) => {
      if (!filterValue) return;
      const runsRef = collection(db, collectionName);
      const q = query(runsRef, where(filterField, "==", filterValue));
      const unsubscribe = onSnapshot(q, (snapshot) => {
        snapshot.docs.forEach((docSnap) => {
          runsMap.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
        });
        upsertRuns();
      });
      unsubscribers.push(unsubscribe);
    };

    subscribeSharedRuns("events", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("runs", "sharedGroupId", shareGroupId);
    subscribeSharedRuns("events", "sharedGroupName", groupName);
    subscribeSharedRuns("runs", "sharedGroupName", groupName);

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [groupName, shareGroupId]);

  useEffect(() => {
    const fetchRacers = async () => {
      if (!shareGroupId || runs.length === 0) {
        setRacers([]);
        return;
      }

      const racerIds = new Set();
      runs.forEach((run) => {
        if (!Array.isArray(run.racers)) return;
        run.racers.forEach((entry) => {
          if (entry?.racerId) {
            racerIds.add(entry.racerId);
          }
        });
      });

      const uniqueIds = Array.from(racerIds);
      if (uniqueIds.length === 0) {
        setRacers([]);
        return;
      }

      const racersRef = collection(db, "timer_racers");
      const chunks = chunkIds(uniqueIds);
      const racerDocs = [];

      for (const chunk of chunks) {
        const q = query(racersRef, where("__name__", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((docSnap) => {
          racerDocs.push({ id: docSnap.id, ...docSnap.data() });
        });
      }

      racerDocs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRacers(racerDocs);
    };

    fetchRacers();
  }, [runs, shareGroupId]);

  if (!shareGroupId) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Missing share link.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <h2>Racers in Public Runs</h2>
      {racers.length === 0 ? (
        <p>No racers available.</p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0 }}>
          {racers.map((racer) => (
            <li
              key={racer.id}
              style={{
                marginBottom: "10px",
                borderBottom: "1px solid #ccc",
                padding: "10px 0",
              }}
            >
              <strong>{racer.name || "Unnamed Racer"}</strong>
              <br />
              <Link to={`/racer/${racer.id}?id=${shareGroupId}`}>
                View Racer
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GuestRacers;
